import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  X,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Tag,
  Search,
  Check,
} from "lucide-react";
import { Range } from "react-range";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
  }).format(value);

// --- Custom Local Dropdown Filter ---
const LocalSearchableFilter = ({
  title,
  options,
  selected,
  onChange,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter((o) =>
    o.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter((item) => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 ml-1">
        {title}
      </label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
      >
        <span className="truncate">
          {selected.length > 0 ? `${selected.length} Selected` : placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 flex flex-col overflow-hidden">
          <div className="p-2 border-b border-gray-100 bg-gray-50/50">
            <div className="relative">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                size={13}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
              />
            </div>
          </div>
          <div className="overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
              <p className="text-center text-xs text-gray-400 py-3">
                No results found
              </p>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selected.includes(opt);
                return (
                  <div
                    key={opt}
                    onClick={() => toggleOption(opt)}
                    className="flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg cursor-pointer transition-colors"
                  >
                    <span className="truncate">{opt}</span>
                    {isSelected && (
                      <Check size={12} className="text-indigo-600 shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Main Advanced Search Component ---
const AddSalesOrderAdvancedSearch = ({
  isOpen,
  onClose,
  products = [],
  activePriceType,
  appliedFilters,
  onApplyFilters,
}) => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Dynamically extract unique values from the loaded products
  const availableOptions = useMemo(() => {
    const extract = (key) =>
      [...new Set(products.map((p) => p[key]).filter(Boolean))].sort();
    return {
      categories: extract("categoryName"),
      categoriesTwo: extract("categoryTwoName"),
      categoriesThree: extract("categoryThreeName"),
      categoriesFour: extract("categoryFourName"),
      categoriesFive: extract("categoryFiveName"),
      brands: extract("brandName"),
    };
  }, [products]);

  // Calculate absolute min and max prices based on current price type
  const [absoluteMin, absoluteMax] = useMemo(() => {
    if (!products.length || !activePriceType) return [0, 100];
    const prices = products.map((p) => parseFloat(p[activePriceType]) || 0);
    const min = Math.floor(Math.min(...prices));
    const max = Math.ceil(Math.max(...prices));
    return [min, max === min ? min + 1 : max]; // Prevent min === max error in slider
  }, [products, activePriceType]);

  // Local state for the modal form
  const [filters, setFilters] = useState({
    categories: [],
    categoriesTwo: [],
    categoriesThree: [],
    categoriesFour: [],
    categoriesFive: [],
    brands: [],
    priceRange: [0, 100], // Temp fallback, will be overwritten by useEffect
  });

  // Sync applied filters when modal opens
  useEffect(() => {
    if (isOpen) {
      setFilters(
        appliedFilters || {
          categories: [],
          categoriesTwo: [],
          categoriesThree: [],
          categoriesFour: [],
          categoriesFive: [],
          brands: [],
          priceRange: [absoluteMin, absoluteMax],
        },
      );
    }
  }, [isOpen, appliedFilters, absoluteMin, absoluteMax]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ✅ SAFELY CLAMP VALUES FOR THE SLIDER
  // This prevents the React-Range "value is smaller than min" error during the first render
  const safePriceRange = useMemo(() => {
    const minVal = Math.max(
      absoluteMin,
      Math.min(filters.priceRange[0] ?? absoluteMin, absoluteMax),
    );
    let maxVal = Math.max(
      absoluteMin,
      Math.min(filters.priceRange[1] ?? absoluteMax, absoluteMax),
    );

    // Ensure array is valid
    if (minVal > maxVal) maxVal = minVal;

    return [minVal, maxVal];
  }, [filters.priceRange, absoluteMin, absoluteMax]);

  const isPriceFiltered =
    safePriceRange[0] > absoluteMin || safePriceRange[1] < absoluteMax;

  const activeFilterCount =
    filters.categories.length +
    filters.categoriesTwo.length +
    filters.categoriesThree.length +
    filters.categoriesFour.length +
    filters.categoriesFive.length +
    filters.brands.length +
    (isPriceFiltered ? 1 : 0);

  const handleReset = () => {
    setFilters({
      categories: [],
      categoriesTwo: [],
      categoriesThree: [],
      categoriesFour: [],
      categoriesFive: [],
      brands: [],
      priceRange: [absoluteMin, absoluteMax],
    });
  };

  const handleApply = () => {
    // Save clamped safe range into the parent
    onApplyFilters({ ...filters, priceRange: safePriceRange });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-gray-200 animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-100 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm">
              <SlidersHorizontal size={15} />
            </div>
            <div>
              <p className="font-bold text-gray-900 leading-tight">
                Advanced Product Filter
              </p>
              <p className="text-[10px] text-gray-400 font-bold tracking-wide mt-0.5 uppercase">
                Filter available stock by specific attributes
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0 space-y-6">
          {/* Primary Filters */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-4 rounded-full bg-indigo-500 shadow-sm shadow-indigo-200" />
              <p className="text-[10px] font-black tracking-widest uppercase text-gray-400">
                Primary Filters
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <LocalSearchableFilter
                title="Category"
                placeholder="All Categories"
                options={availableOptions.categories}
                selected={filters.categories}
                onChange={(v) => updateFilter("categories", v)}
              />
              <LocalSearchableFilter
                title="Sub-Category"
                placeholder="All Sub-Categories"
                options={availableOptions.categoriesTwo}
                selected={filters.categoriesTwo}
                onChange={(v) => updateFilter("categoriesTwo", v)}
              />
              <LocalSearchableFilter
                title="Brand"
                placeholder="All Brands"
                options={availableOptions.brands}
                selected={filters.brands}
                onChange={(v) => updateFilter("brands", v)}
              />
            </div>
          </div>

          <div className="h-px bg-gray-100" />

          {/* Extended Filters */}
          <div>
            <button
              onClick={() => setShowMoreFilters(!showMoreFilters)}
              className="flex items-center gap-2 w-full group mb-2"
            >
              <span
                className={`w-1.5 h-4 rounded-full transition-colors shadow-sm ${showMoreFilters ? "bg-indigo-400 shadow-indigo-200" : "bg-gray-200"}`}
              />
              <p
                className={`text-[10px] font-black tracking-widest uppercase transition-colors ${showMoreFilters ? "text-indigo-600" : "text-gray-400 group-hover:text-gray-600"}`}
              >
                Extended Hierarchy
              </p>
              <div className="ml-auto text-gray-300 group-hover:text-indigo-400 transition-colors">
                {showMoreFilters ? (
                  <ChevronUp size={14} />
                ) : (
                  <ChevronDown size={14} />
                )}
              </div>
            </button>

            {showMoreFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 p-5 mt-4 bg-gray-50/50 rounded-xl border border-gray-100">
                <LocalSearchableFilter
                  title="Level 3 Category"
                  placeholder="Any"
                  options={availableOptions.categoriesThree}
                  selected={filters.categoriesThree}
                  onChange={(v) => updateFilter("categoriesThree", v)}
                />
                <LocalSearchableFilter
                  title="Level 4 Category"
                  placeholder="Any"
                  options={availableOptions.categoriesFour}
                  selected={filters.categoriesFour}
                  onChange={(v) => updateFilter("categoriesFour", v)}
                />
                <LocalSearchableFilter
                  title="Level 5 Category"
                  placeholder="Any"
                  options={availableOptions.categoriesFive}
                  selected={filters.categoriesFive}
                  onChange={(v) => updateFilter("categoriesFive", v)}
                />
              </div>
            )}
          </div>

          <div className="h-px bg-gray-100" />

          {/* Price Range */}
          {absoluteMin < absoluteMax && activePriceType && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-1.5 h-4 rounded-full bg-indigo-500 shadow-sm shadow-indigo-200" />
                <p className="text-[10px] font-black tracking-widest uppercase text-gray-400">
                  Price Range ({activePriceType})
                </p>
              </div>
              <div className="bg-gray-50/50 rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                    <Tag size={12} />
                    <span>Drag to restrict prices</span>
                  </div>
                  <span className="text-xs font-black text-indigo-700 bg-indigo-50 border border-indigo-200 px-3 py-1 rounded-md shadow-sm">
                    {formatCurrency(safePriceRange[0])} —{" "}
                    {formatCurrency(safePriceRange[1])}
                  </span>
                </div>
                <div className="px-3 pb-2">
                  <Range
                    step={1}
                    min={absoluteMin}
                    max={absoluteMax}
                    values={safePriceRange} // ✅ Using safe clamped array
                    onChange={(vals) => updateFilter("priceRange", vals)}
                    renderTrack={({ props, children }) => (
                      <div
                        {...props}
                        style={{
                          ...props.style,
                          height: "6px",
                          width: "100%",
                          position: "relative",
                        }}
                        className="rounded-full bg-gray-200 shadow-inner"
                      >
                        <div
                          style={{
                            position: "absolute",
                            height: "6px",
                            borderRadius: "6px",
                            background: "#4f46e5",
                            left: `${((safePriceRange[0] - absoluteMin) / (absoluteMax - absoluteMin)) * 100}%`,
                            width: `${((safePriceRange[1] - safePriceRange[0]) / (absoluteMax - absoluteMin)) * 100}%`,
                          }}
                        />
                        {children}
                      </div>
                    )}
                    renderThumb={({ props }) => (
                      <div
                        {...props}
                        style={{ ...props.style }}
                        className="w-5 h-5 rounded-full bg-white border-4 border-indigo-600 shadow-md outline-none cursor-pointer hover:scale-110 transition-transform"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-2xl shrink-0">
          <p className="text-xs font-bold text-gray-400">
            {products.length} total products loaded for this location.
          </p>
          <div className="flex gap-3">
            {activeFilterCount > 0 && (
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-black text-red-500 bg-red-50 border border-red-200 hover:bg-red-100 hover:text-red-600 transition-colors shadow-sm"
              >
                <RotateCcw size={13} />
                Reset
              </button>
            )}
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-6 py-2.5 rounded-xl text-sm font-black text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-100 active:scale-95 transition-all"
            >
              Apply {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSalesOrderAdvancedSearch;
